import { Component, Inject, OnInit, Optional } from '@angular/core';
import { ListButton } from '@app/core/components/list/list.component';
import { LayoutService } from '@app/core/services/layout.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { BusinesscoItem, LogisticService } from '@app/services/logistic.service';
import { SellerItem, SellersGet, SellerService } from '@app/services/seller.service';

@Component({
    selector: 'tt-sellers',
    templateUrl: './sellers.component.html',
    styleUrls: ['./sellers.component.css'],
    standalone: false
})
export class SellersComponent implements OnInit {
    public page?: SellersGet;
    
    public sellers: SellerItem[] = [];
    
    public companies: BusinesscoItem[] = [];
    
    public itemState = '';
    
    public sellerId = '';
    
    public readonly tablet: MediaQueryList;
    
    public readonly rowButtons: ListButton<SellerItem>[] = [{
        id: 'new-tab',
        icon: 'fas fa-window-restore',
        type: 'primary',
        onClick: ({ item }) => {
            if (item.item_state) {
                this.navigator?.newTab({
                    to: item.item_state,
                    parms: item.item_parms
                });
            }
        }
    }];

    constructor(
        private seller: SellerService,
        private logistic: LogisticService,
        layout: LayoutService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator
    ) {
        this.navigator = useNavigatorFor(navigator, this);
        this.tablet = layout.getMediaQueries().tablet;
    }

    public get inlineEditor(): boolean {
        return this.tablet.matches && this.itemState === 'seller';
    }
    
    private async loadPage(): Promise<void> {
        this.page = await this.seller.getSettings();
    }

    private async loadCompanies(): Promise<void> {
        this.companies = await this.logistic.getCompanies('1');
    }

    public async loadSellers(): Promise<void> {
        if (!this.page) return;
        
        this.sellers = await this.seller.listSellers(this.page);
        
        // Preserve the last nonempty state, including when a later result is empty.
        for (const item of this.sellers) {
            if (item.item_state > '') {
                this.itemState = item.item_state;
            }
        }
    }
    
    public async updateList(): Promise<void> {
        this.sellerId = '';
        await this.loadSellers();
    }
    
    public async changeTab(value: string): Promise<void> {
        if (!this.page) return;

        this.page.gb_sellers_main = value;
        
        await this.loadSellers();
    }

    public async changeCompany(value: string): Promise<void> {
        if (!this.page) return;
        
        this.page.businessco_no = value;
        
        if (value == null) return;
        
        await this.loadSellers();
    }
    
    public openItem({ item, event }: { item: SellerItem; event: MouseEvent }): void {
        if (this.inlineEditor) {
            this.sellerId = item.item_id;
            return;
        }
        
        if (item.item_state === 'customaction') {
            item.item_action?.(item);
            return;
        }
        
        if (!item.item_state || item.item_path === '') return;
        
        const target = {
            to: item.item_state,
            parms: item.item_parms
        };
        
        if (event.ctrlKey) this.navigator?.newTab(target);
        else this.navigator?.go(target);
    }

    public createSeller(event?: MouseEvent): void {
        if (this.tablet.matches && this.itemState === 'seller') {
            this.sellerId = '0';
            return;
        }

        this.navigator?.go({
            to: 'seller',
            parms: { seller_id: '0' }
        });
    }

    public async ngOnInit(): Promise<void> {
        await Promise.all([
            this.loadPage(),
            this.loadCompanies()
        ]);

        this.loadSellers();
    }
}
